
import Vue from "vue";
import draggable from "vuedraggable";
import { Component, Prop } from "vue-property-decorator";
import {
  Control,
  decorateValueEmittedByInputWithMetadata
} from "@/types/controls";

import defensiveCopy from "@/util/defensive-copy";
import RepeaterItemTitleText from "@/components/Reusable/CustomInputs/Repeater/RepeaterItemTitleText.vue";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { renderRepeaterVariables } from "@/util/repeaters";
import { configStore } from "@/store/store";

@Component({
  components: {
    NiceButton,
    Icon,
    RepeaterItemTitleText,
    draggable
  }
})
export default class InlineRepeater extends Vue {
  @Prop()
  control!: Control;

  @Prop()
  value!: unknown[];

  @Prop({ default: false })
  createSnapshotWhenItemsListChanges!: boolean;

  @Prop()
  defaultNewValue!: unknown;

  @Prop({ default: false })
  dense!: boolean;

  @Prop({ default: true })
  canDrag!: boolean;

  @Prop({ default: true })
  canAdd!: boolean;

  @Prop({ default: true })
  canDelete!: boolean;

  @Prop()
  dataKey!: string;

  @Prop()
  group?: string;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.ControlsTree =
      require("@/components/Reusable/ControlsTree/ControlsTree.vue").default;
  }

  dragOptions = {
    animation: 200,
    handle: ".breakdance-inline-repeater-drag-handle",
    ghostClass: "breakdance-inline-repeater-item--ghost",
    forceFallback: true
  };

  get builderMode() {
    return configStore.builderMode;
  }

  get buttonName() {
    return this.control?.options.repeaterOptions?.buttonName || "Add new";
  }

  get templateItems() {
    return this.control?.options.repeaterOptions?.templates || [];
  }

  get autofocusOnAdd() {
    return this.control?.options.repeaterOptions?.autofocusOnAdd || false;
  }

  get autofocusIndex() {
    return this.control?.options.repeaterOptions?.autofocusIndex || 0;
  }

  emit(newValue: unknown) {
    this.$emit(
      "input",
      this.createSnapshotWhenItemsListChanges
        ? decorateValueEmittedByInputWithMetadata(newValue, {
            disableTransactionBundling: true
          })
        : newValue
    );
  }

  handleDrag(newValue: unknown) {
    this.emit(defensiveCopy(newValue));
  }

  addItem() {
    if (this.templateItems.length) {
      return;
    }

    const repeaterData: Array<unknown> = defensiveCopy(this.value) || [];
    repeaterData.push(this.processDefaultValue(this.defaultNewValue) || {});

    this.emit(repeaterData);

    this.maybeFocusNewItem();
  }

  deleteItem(index: number) {
    const repeaterData = defensiveCopy(this.value) || [];
    repeaterData.splice(index, 1);

    this.emit(repeaterData);
  }

  addTemplateItem(value: unknown) {
    const repeaterData = defensiveCopy(this.value) || [];
    repeaterData.push(this.processDefaultValue(value));

    this.emit(repeaterData);

    this.maybeFocusNewItem();
  }

  processDefaultValue(newValue: unknown) {
    return renderRepeaterVariables(newValue, this.value?.length || 0);
  }

  maybeFocusNewItem() {
    this.$nextTick(() => {
      if (!this.autofocusOnAdd) return;

      const lastItemIndex = this.value.length - 1;
      const items = this.$refs.items as HTMLElement[] | undefined;
      const lastItem = items?.[lastItemIndex];

      if (lastItem) {
        const inputs = lastItem.querySelectorAll("input");
        const input = inputs[this.autofocusIndex] || inputs[0];

        if (!input) return;

        this.$nextTick(() => input.focus());
      }
    });
  }
}
